import { useEffect, useState } from 'react';
import { Card, DataTable, FilterBar, Select, Stat, StatusBadge, PageHeader } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface Inv { id: number; tenant_id: number; number: string; period: string; amount_cents: number; status: string; due_at?: string }

export function Invoices() {
  const [rows, setRows] = useState<Inv[]>([]);
  const [summary, setSummary] = useState<{ paid: number; pending: number; overdue: number }>({ paid: 0, pending: 0, overdue: 0 });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { void load(); }, [status]);

  async function load() {
    setLoading(true);
    try {
      const res = await superApi.invoices({ status: status || undefined });
      setRows(res.data as Inv[]);
      const meta = (res as unknown as { meta?: { summary?: { paid: number; pending: number; overdue: number } } }).meta;
      // Merge over defaults — a partial summary from the API must not crash
      // the Stat cards (each value goes through .toFixed).
      setSummary({ paid: 0, pending: 0, overdue: 0, ...(meta?.summary ?? {}) });
    } catch {
      toast('שגיאה בטעינת החשבוניות', 'danger');
    } finally {
      setLoading(false);
    }
  }

  async function markPaid(id: number) {
    try {
      await superApi.markInvoicePaid(id);
      toast('סומן כשולם', 'ok');
      void load();
    } catch {
      toast('שגיאה בסימון התשלום', 'danger');
    }
  }

  const cols: DataTableColumn<Inv>[] = [
    { key: 'number', he: 'מספר חשבונית' },
    { key: 'tenant_id', he: 'לקוח (ID)' },
    { key: 'period', he: 'תקופה' },
    { key: 'amount_cents', he: 'סכום', render: (r) => `₪${(r.amount_cents / 100).toFixed(2)}` },
    { key: 'status', he: 'סטטוס', render: (r) => <StatusBadge tone={r.status === 'paid' ? 'ok' : r.status === 'pending' ? 'warn' : 'danger'} dot size="sm">{r.status}</StatusBadge> },
    { key: '_actions', he: '', render: (r) => r.status !== 'paid' && <button type="button" className="btn ghost" onClick={() => markPaid(r.id)}>סמן כשולם</button> },
  ];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="חשבוניות" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
        <Stat label="שולמו" value={`₪${summary.paid.toFixed(0)}`} />
        <Stat label="ממתינות" value={`₪${summary.pending.toFixed(0)}`} />
        <Stat label="באיחור" value={`₪${summary.overdue.toFixed(0)}`} />
      </div>
      <FilterBar>
        <Select value={status} onChange={setStatus} options={[
          { value: '', label: 'הכל' },
          { value: 'paid', label: 'שולמו' },
          { value: 'pending', label: 'ממתינות' },
          { value: 'overdue', label: 'באיחור' },
        ]} />
      </FilterBar>
      <Card padded={false}><DataTable<Inv> columns={cols} rows={rows} loading={loading} /></Card>
    </div>
  );
}
